import React from 'react'
import Link from 'next/link'
import Image from 'next/image'

import { GuidesSearch } from '@/components/atoms/GuidesSearch'
import { PrimaryLayout } from '@/components/templates'
import techHealth from 'public/guides/tech-in-health.png'
import tips from 'public/guides/tips-tricks.png'
import healthy from 'public/guides/staying-healthy.png'
import medicare from 'public/guides/medicare-guide.png'

import { SEO } from '../..'

export default function GuidesLanding({ posts }: any) {
  const [searchBarValue, setSearchBarValue] = React.useState('')
  const [matchedTitles, setMatchedTitles] = React.useState<string[]>([])
  const [showDropdown, setShowDropdown] = React.useState(false)
  const [postLinks, setPostLinks] = React.useState<string[]>([])

  const guides = [
    {
      category: 'Staying Healthy',
      src: healthy.src,
      title: 'Staying Healthy',
    },
    {
      category: 'Health Insurance Tips & Tricks',
      src: tips.src,
      title: 'Health Insurance Tips & Tricks',
    },
    {
      category: 'Medicare Guide',
      src: medicare.src,
      title: 'Medicare Guide',
    },
    {
      category: 'Technology in Health',
      src: techHealth.src,
      title: 'Technology in Health',
    },
  ]

  const handleSearch = (query: string) => {
    const matched = posts.filter((post: { title: string }) =>
      post.title.toLowerCase().includes(query.toLowerCase()),
    )
    const matchedTitles = matched.map((post: { title: any }) => post.title)
    const links = matched.map(
      (post: { categories: string[]; slug: any }) =>
        `/guides/${encodeURIComponent(post.categories[0])}/${post.slug}`,
    )
    setMatchedTitles(matchedTitles)
    setSearchBarValue(query)
    setShowDropdown(matchedTitles.length > 0)
    setPostLinks(links)
  }

  const handleBlur = () => {
    setTimeout(() => {
      setShowDropdown(false)
    }, 2000)
  }

  return (
    <>
      <PrimaryLayout>
        <SEO
          title={`RateQuote Guides - Insights and Tips for Insurance Plans`}
          description={`RateQuote Guides - Insights and Tips for Insurance Plans`}
        />
        {/* Container */}
        <div className="px-4 sm:px-0">
          {/* Top Area */}
          <div className="flex gap-6 items-end flex-wrap">
            {/* Headings */}
            <div className="sm:w-[calc(50%-12px)] w-full">
              <h1 className="font-circular text-blue-navy text-4xl sm:text-5xl">
                Explore our <span className="text-blue-primary">guides</span>
              </h1>
              <p className="font-dm text-grayscale-7 text-base tracking-wider py-3 px-0.5">
                Learn more about health insurance with our broad range of
                articles
              </p>
            </div>
            {/* Search */}
            <div className="sm:w-[calc(50%-12px)] w-full relative flex justify-end">
              <GuidesSearch
                autoComplete="off"
                type="text"
                name="search"
                placeholder="Search a topic"
                value={searchBarValue}
                onChange={(e) => handleSearch(e.target.value)}
                onBlur={handleBlur}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-search absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none stroke-grayscale-1"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                }
              />
              {showDropdown && (
                <div className="max-h-200px max-w-md absolute z-10 w-full overflow-y-auto bg-white shadow-lg right-0 top-12 rounded-xs">
                  {matchedTitles.map((title, index) => (
                    <Link
                      key={index}
                      href={postLinks[index]}
                      passHref
                      className="hover:text-blue-primary block cursor-pointer p-2 hover:bg-gray-100"
                    >
                      {title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Guides Categories */}
          <ul className="flex gap-6 mt-8 flex-wrap">
            {guides.map((guide, index) => (
              <li
                key={index}
                className="overflow-hidden z-0 md:w-[calc(25%-18px)] w-[calc(50%-12px)] group rounded-sm shadow-lg"
              >
                <Link href={`/guides/${encodeURIComponent(guide.category)}/`}>
                  <div className="relative">
                    <Image
                      src={guide.src}
                      alt={guide.title}
                      width={360}
                      height={640}
                      className="object-cover w-full group-hover:scale-110 transition-transform duration-500 ease-in-out aspect-[9/14]"
                      priority
                    />
                    <div className="bg-gradient-to-t from-grayscale-4/80 from-10% via-white/0 to-white/0 absolute inset-0 flex items-end justify-center">
                      <p className="font-circular py-5 px-2 text-white text-center text-base md:text-lg">
                        {guide.title}
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {/* Trending Articles */}
          <div className="my-12">
            <h2 className="font-circular text-blue-navy text-3xl sm:text-4xl">
              Trending articles
            </h2>
            <ul className="flex gap-6 mt-8 flex-wrap">
              {posts.slice(0, 4).map((post: any, index: any) => (
                <li
                  key={index}
                  className="flex gap-3 z-0 md:w-[calc(25%-18px)] xs:w-[calc(50%-12px)] w-full shadow-lg rounded-sm overflow-hidden group bg-white"
                >
                  <Link
                    href={`/guides/${post.categories[0].replace(/\W+(?!$)/g, '-')}/${post.slug}`}
                    className="w-full"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <Image
                        src={post.image}
                        alt={post.title}
                        width={640}
                        height={320}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ease-in-out"
                      />
                    </div>
                    <div className="p-2 mt-2">
                      <p className="font-dm text-blue-primary text-sm py-1 px-2 flex">
                        {post.categories}
                      </p>
                      <p className="font-circular text-blue-navy text-base mt-2 py-1 px-2">
                        {post.title}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </PrimaryLayout>
    </>
  )
}
